import { useEffect } from "react";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function HomeSEO() {
  useEffect(() => {
    document.title =
      "Crown Platform — Jobs, Ads & Business Marketplace | New York, London, Dubai, Sydney";

    let desc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content =
      "Crown Platform is the #1 global jobs, ads & business marketplace in New York, London, Dubai, Sydney, Toronto, Paris & more. Post jobs, ads, factory products, dropshipping & connect with a worldwide community.";

    let keywords = document.querySelector('meta[name="keywords"]') as HTMLMetaElement | null;
    if (!keywords) {
      keywords = document.createElement("meta");
      keywords.name = "keywords";
      document.head.appendChild(keywords);
    }
    keywords.content =
      "crown platform, jobs marketplace, business ads, global factory, dropshipping, community charity, buyer requests, social stars";

    const existingScript = document.getElementById("home-jsonld");
    if (existingScript) existingScript.remove();

    const script = document.createElement("script");
    script.id = "home-jsonld";
    script.type = "application/ld+json";
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: "Crown Platform",
        url: SITE_URL,
        description:
          "The #1 global crown platform for jobs, ads, business marketplace, dropshipping & community charity. Active in 30+ cities worldwide.",
        inLanguage: "en-US",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${SITE_URL}/?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Crown Platform",
        url: SITE_URL,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/logo.png`,
          width: 200,
          height: 60,
        },
        description:
          "Crown Platform connects job seekers, advertisers, factories, sellers, buyers, and social media influencers across 30+ cities in USA, Canada, Europe, Asia, and Australia.",
        foundingDate: "2024",
        areaServed: [
          { "@type": "City", name: "New York" },
          { "@type": "City", name: "Los Angeles" },
          { "@type": "City", name: "Chicago" },
          { "@type": "City", name: "Florida" },
          { "@type": "City", name: "Vancouver" },
          { "@type": "City", name: "Toronto" },
          { "@type": "City", name: "London" },
          { "@type": "City", name: "Paris" },
          { "@type": "City", name: "Berlin" },
          { "@type": "City", name: "Dubai" },
          { "@type": "City", name: "Tokyo" },
          { "@type": "City", name: "Singapore" },
          { "@type": "City", name: "Sydney" },
          { "@type": "City", name: "Melbourne" },
        ],
        sameAs: [
          "https://www.facebook.com/crownplatform",
          "https://www.twitter.com/crownplatform",
          "https://www.instagram.com/crownplatform",
          "https://www.youtube.com/crownplatform",
          "https://www.tiktok.com/@crownplatform",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          availableLanguage: ["English"],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${SITE_URL}/#webpage`,
        url: SITE_URL,
        name: "Crown Platform — Jobs, Ads & Business Marketplace",
        description:
          "Crown Platform is the #1 global jobs, ads & business marketplace in New York, London, Dubai, Sydney, Toronto, Paris & more.",
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en-US",
        dateModified: "2026-04-10",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: SITE_URL,
            },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Crown Platform Services",
        description: "Core services offered by Crown Platform globally.",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            item: {
              "@type": "Service",
              name: "Job Post Board",
              description: "Post and find jobs across 30+ cities worldwide on Crown Platform.",
              url: `${SITE_URL}/#jobs`,
              provider: { "@id": `${SITE_URL}/#organization` },
            },
          },
          {
            "@type": "ListItem",
            position: 2,
            item: {
              "@type": "Service",
              name: "Industry Ads",
              description: "Advertise your business to millions of users across USA, Canada, Europe, Asia, and Australia.",
              url: `${SITE_URL}/#ads`,
              provider: { "@id": `${SITE_URL}/#organization` },
            },
          },
          {
            "@type": "ListItem",
            position: 3,
            item: {
              "@type": "Service",
              name: "Global Factory Marketplace",
              description: "Connect with factories and manufacturers worldwide for bulk orders and wholesale products.",
              url: `${SITE_URL}/#factory`,
              provider: { "@id": `${SITE_URL}/#organization` },
            },
          },
          {
            "@type": "ListItem",
            position: 4,
            item: {
              "@type": "Service",
              name: "Buyer Requests",
              description: "Post your purchasing requirements for FREE and receive quotes from verified sellers globally.",
              url: `${SITE_URL}/#buyer-requests`,
              provider: { "@id": `${SITE_URL}/#organization` },
            },
          },
          {
            "@type": "ListItem",
            position: 5,
            item: {
              "@type": "Service",
              name: "Warehouse Dropshipping",
              description: "Suppliers upload products, sellers list them, and social media influencers earn commissions promoting them.",
              url: `${SITE_URL}/#dropshipping`,
              provider: { "@id": `${SITE_URL}/#organization` },
            },
          },
          {
            "@type": "ListItem",
            position: 6,
            item: {
              "@type": "Service",
              name: "Social Stars Commission",
              description: "Facebook, TikTok, Twitter, and YouTube influencers earn commissions by promoting dropshipping products.",
              url: `${SITE_URL}/#social-stars`,
              provider: { "@id": `${SITE_URL}/#organization` },
            },
          },
          {
            "@type": "ListItem",
            position: 7,
            item: {
              "@type": "Service",
              name: "News & Lottery Guess",
              description: "Community lottery guesses with charity donations for infants in need worldwide.",
              url: `${SITE_URL}/#news-lottery`,
              provider: { "@id": `${SITE_URL}/#organization` },
            },
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "SiteLinksSearchBox",
        url: SITE_URL,
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is Crown Platform?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Crown Platform is the #1 global marketplace for jobs, advertisements, factory products, business buying & selling, dropshipping, lottery guesses, and community charity — active in 30+ cities across USA, Canada, Europe, Asia, and Australia.",
            },
          },
          {
            "@type": "Question",
            name: "Is Crown Platform free to use?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Buyers can post requests for FREE. Sellers, factories, advertisers, and job posters need a Basic ($5/mo) or Gold ($18/mo) membership.",
            },
          },
          {
            "@type": "Question",
            name: "Which cities does Crown Platform serve?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Crown Platform serves 30+ cities including New York, Florida, Los Angeles, Chicago, Vancouver, Toronto, London, Paris, Berlin, Dubai, Tokyo, Singapore, Sydney, and Melbourne.",
            },
          },
          {
            "@type": "Question",
            name: "How does the Crown Platform dropshipping work?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Suppliers upload products to the Crown Platform warehouse. Sellers browse and list those products in their own storefront. Social media influencers (Facebook, TikTok, Twitter, YouTube) promote the products and earn commissions on every sale.",
            },
          },
          {
            "@type": "Question",
            name: "How can social media influencers earn on Crown Platform?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Facebook, TikTok, Twitter, and YouTube stars can apply as influencers on Crown Platform. They promote dropshipping products and earn a commission percentage on every sale they generate. The leaderboard tracks products sold and total commission earned.",
            },
          },
          {
            "@type": "Question",
            name: "What is the Lottery Guess & Charity section?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Community members post their lottery number guesses. If someone wins using a shared guess, they are encouraged to donate for infants in need around the world — providing milk, warm clothes, and care.",
            },
          },
        ],
      },
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById("home-jsonld");
      if (s) s.remove();
    };
  }, []);

  return null;
}
